import { useEffect, useState, useContext } from 'react';
import type { JoinFormType, PackagePrices, RapnetRegistrationRequest } from 'types/join';
import styled from 'styled-components';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import {
  LastSubscriptionRequest,
  createHubSpotForm,
  getRegistrationCurrentContract,
  ConfirmAndCheckout,
  sendComplianceDocsRequiredEmail
} from 'components/join/actions/index';
import { TranslationContext } from 'contexts/translation-context';
import { openModal } from 'components/common/modal/modal-actions';
import { useSessionStorage } from 'hooks/useSessionStorage';
import { CreateHubSpotForm } from 'utils/create-hbspt-form';
import { errorList, errorListTypes } from 'constants/error-list';
import { useExecuteRecaptcha } from 'hooks/useExecuteRecaptcha';
import { useRegistrationPrefill } from 'components/join/hooks/use-registration-prefill';
import { deleteRapaportUserData } from 'utils/cookie-user-data';
import { PlanState } from '../reducers/plan';
import { SuccessPage } from './success-page';
import ConfirmAndCheckoutNotSuccessModal from './confirm-and-checkout-not-success';
import SuccessPageForPriceList from './success-pricelist-afetr-payment';
import ThankYouScreenForPriceList from './contect-me-for-pricelist';
import { HeaderFooter } from './header-footer';
import { JoinForm } from './join-form';
import SubscriptionAssociatedModal from './subscription-associated-modal';
import SuspendedErrorModal from './suspended-error-modal';
import AcountExistsInCompanyErrorModal from './acount-exists-in-company-error-modal';
import { YourPlanContainer } from './your-plan-container';
import { PaymentDetailsSection } from './payment-details';

const Wrapper = styled.div<{ isPriceList?: boolean }>`
  display: flex;
  justify-content: center;
  position: relative;
  background-color: ${props => props.theme.colors.siteGrey};
  font-family: ${props => props.theme.fonts.interSans};
  padding: 32px 24px;
  flex: 1;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 999px) {
    padding: 16px 0 24px;
  }
`;

const PageCard = styled.div`
  background-color: ${props => props.theme.colors.white};
  border-radius: 24px;
  width: 100%;
  max-width: 1180px;
  padding: 40px 30px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
  box-sizing: border-box;

  @media (max-width: 1199px) {
    padding: 36px 36px;
    max-width: 1000px;
  }

  @media (max-width: 999px) {
    border-radius: 12px;
    padding: 24px 16px;
    margin: 0 12px;
    width: auto;
    max-width: none;
  }
`;

const PageTitle = styled.h1`
  font-family: ${props => props.theme.fonts.FunnelDisplay};
  font-size: 36px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.12;
  color: ${props => props.theme.colors.black};
  text-align: center;
  margin: 0 0 8px;

  @media (max-width: 999px) {
    font-size: 20px;
  }
`;

const ChargeNotice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  box-sizing: border-box;
  background: #fdf6e7;
  border: 1px solid #e8c97e;
  border-radius: 8px;
  padding: 16px 20px;
  margin: 0 0 32px;
  text-align: left;

  @media (max-width: 999px) {
    padding: 12px 14px;
    margin-bottom: 20px;
  }
`;

const ChargeNoticeIcon = styled.span`
  flex-shrink: 0;
  font-size: 18px;
  line-height: 1.3;
  color: #b07d1e;
`;

const ChargeNoticeTitle = styled.p`
  font-family: ${props => props.theme.fonts.interSans};
  font-size: 16px;
  font-weight: 700;
  line-height: 1.4;
  color: #7a5a12;
  margin: 0 0 4px;

  @media (max-width: 999px) {
    font-size: 14px;
  }
`;

const ChargeNoticeText = styled.p`
  font-family: ${props => props.theme.fonts.interSans};
  font-size: 14px;
  line-height: 1.5;
  color: #8a6d2b;
  margin: 0;

  @media (max-width: 999px) {
    font-size: 12px;
  }
`;

const PanelsRow = styled.div`
  display: grid;
  grid-template-columns: minmax(320px, 380px) 1fr;
  gap: 32px;
  align-items: flex-start;

  @media (max-width: 999px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const Container = styled.div<{ planBoxWidth: number }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;

  @media (max-width: 999px) {
    width: 100%;
  }
`;

interface CustomWindow extends Window {
  dataLayer?: any[];
}

const isUnitedStatesCountry = (country: string | null) => {
  const normalizedCountry = country?.trim().toLowerCase();
  return normalizedCountry === 'usa' || normalizedCountry === 'united states';
};

export const JoinManger = () => {
  const TRANSLATION_TEXT = useContext(TranslationContext) as { [key: string]: string };
  const dispatch = useDispatch();
  const [planBoxWidth, setPlanBox] = useState(window.innerWidth < 1200 ? 345 : 430);
  const { execute } = useExecuteRecaptcha();
  const customWindow: CustomWindow & typeof globalThis = window;

  useEffect(() => {
    if (window.innerWidth) {
      setPlanBox(window.innerWidth < 1200 ? 345 : 430);
    }
  }, [window.innerWidth]);

  const {
    subscriptionId,
    opportunityId,
    paymentLink,
    currentContract,
    showErrorPopup,
    sfContactId,
    showSuccessPageForFreePlan,
    companyTypes
  } = useSelector((state: { plan: PlanState }) => state.plan) || {
    currentContract: null
  };

  const [isYourPlanActive, setIsYourPlanActive] = useState(true);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [showSuccessPage, setShowSuccessPage] = useState(false);
  const [showSuccessPageForPriceList, setShowSuccessPageForPriceList] = useState(false);
  const [contactMEThankYouScreenForPriceList] = useState(false);
  const [selectedAddons, setSelectedAddons] = useState<Array<PackagePrices | undefined>>([]);
  const [selectedPlan, setSelectedPlan] = useState<PackagePrices | undefined>();
  const [selectedBilledOption, setSelctedBilledOption] = useState('1 Year');
  const [numConfirmClicked, setNumConfirmClicked] = useState(0);
  const [isPriceList, setIsPriceList] = useState(false);
  const [appliedPromoCodeId, setAppliedPromoCodeId] = useState<string | null>(null);
  const errorMessageType = errorList[showErrorPopup.errorMessage || ''] || '';
  const [finalTotalPrice, setFinalTotalPrice] = useState(0);

  const getInitialValues = () => {
    return {
      service: null,
      initialPlan: null,
      initialPlanPeriod: null,
      firstName: null,
      lastName: null,
      email: null,
      companyName: null,
      companyType: null,
      phone: null,
      cellPhone: null,
      address: null,
      website: null,
      city: null,
      zipCode: null,
      companyCountry: null,
      agree: false,
      smsTerms: true,
      newsBriefNewsletter: false,
      tradeWireNewsletter: false,
      weeklyJewelryNewsletter: false,
      biMonthlyMagazineOnline: false,
      autoRenew: true,
      accountState: null,
      fromCache: false,
      recaptcha: null,
      hasMagazineAddon: false
    };
  };

  const [cacheForm, setCacheForm, removeCacheForm] = useSessionStorage<JoinFormType>(
    'RapnetRegistrationjoinForm',
    getInitialValues()
  );

  const form = useFormik<JoinFormType>({
    initialValues: getInitialValues(),
    onSubmit: values => {
      if (!values.agree) {
        form.setErrors({ agree: 'Please agree to the terms' });
      } else {
        try {
          if (selectedPlan && (!subscriptionId || isYourPlanActive)) {
            setNumConfirmClicked(numConfirmClicked + 1);
            const subscriptionRequestObject = handleConfirmAndCheckout(values.autoRenew);
            if (subscriptionRequestObject) {
              dispatch(ConfirmAndCheckout(subscriptionRequestObject));
            }
          }
        } catch (error) {
          // console.error('Error submitting form:', error);
        }
      }
    },
    validate: values => {
      const errors: any = {};
      const emailValidation =
        /^$|^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))\s?$/;

      if (!values.agree) errors.agree = 'Please agree to the terms';
      if (!values.firstName || values.firstName.trim() === '') errors.firstName = 'Required';
      if (values.firstName && values.firstName.length > 50)
        errors.firstName = 'Can be up to 50 characters long';
      if (!values.lastName || values.lastName.trim() === '') errors.lastName = 'Required';
      if (values.lastName && values.lastName.length > 50)
        errors.lastName = 'Can be up to 50 characters long';

      if (!values.email || values.email.trim() === '') {
        errors.email = 'Required';
      } else if (values.email && values.email.length > 50) {
        errors.email = 'Can be up to 50 characters long';
      } else if (!emailValidation.test(values.email)) {
        errors.email = 'The email address is not valid.';
      }

      if (!values.companyCountry || values.companyCountry.trim() === '')
        errors.companyCountry = 'Required';
      if (values.companyName && values.companyName.length > 50)
        errors.companyName = 'Can be up to 50 characters long';

      if (!isPriceList) {
        if (!values.companyType || values.companyType.trim() === '')
          errors.companyType = 'Required';
        if (!values.companyName || values.companyName.trim() === '')
          errors.companyName = 'Required';
        if (!values.cellPhone || values.cellPhone.trim() === '') {
          errors.cellPhone = 'Required';
        } else {
          const mobileDigits = values.cellPhone.replace(/\D/g, '');
          if (mobileDigits.length < 5) {
            errors.cellPhone = 'Please enter a valid mobile number';
          }
        }
      }

      // The office number is optional on Rapnet, where the mobile number is the mandatory one,
      // but a Price List sign-up has no mobile field and must carry a phone number.
      if (!values.phone || values.phone.trim() === '') {
        if (isPriceList) errors.phone = 'Required';
      } else {
        // Check if phone has digits beyond just the country code (at least 4 digits after country code)
        const phoneDigits = values.phone.replace(/\D/g, '');
        // Most country codes are 1-3 digits, so we need at least 4 more digits for a valid number
        if (phoneDigits.length < 5) {
          errors.phone = 'Please enter a valid phone number';
        }
      }

      if (!isPriceList || form.values.hasMagazineAddon) {
        if (!values.city || values.city.trim() === '') errors.city = 'Required';
        if (!values.address || values.address.trim() === '') errors.address = 'Required';
        if (!values.zipCode || values.zipCode.toString().trim() === '') errors.zipCode = 'Required';
        // Salesforce field limits: city 40, zip code 20, street 255
        if (values.city && values.city.length > 40) errors.city = 'Can be up to 40 characters long';
        if (values.zipCode && values.zipCode.toString().length > 20)
          errors.zipCode = 'Can be up to 20 characters long';
        if (values.address && values.address.length > 255)
          errors.address = 'Can be up to 255 characters long';
        if (
          isUnitedStatesCountry(values.companyCountry) &&
          (!values.accountState || values.accountState.trim() === '')
        ) {
          errors.accountState = 'Required';
        }
      }
      return errors;
    }
  });

  const handleConfirmAndCheckout = (autoRenew: boolean) => {
    let leadsource = 'Registration - Rapaport Trade';
    if (selectedPlan?.subscriptionType === 'PriceList') {
      leadsource = 'Registration - Rapaport';
    }
    const subscriptionRequestObject: RapnetRegistrationRequest = {
      Recaptcha: form.values.recaptcha || '',
      RenewalDate: new Date().toISOString(),
      MainPackageName: selectedPlan?.packageName || '',
      MainPackageId: selectedPlan?.packageId || '',
      MainPackagePeriod:
        selectedBilledOption === TRANSLATION_TEXT.billed_annually ||
        selectedBilledOption === TRANSLATION_TEXT.one_year
          ? '1 Year'
          : 'Monthly',
      TotalAmount:
        (selectedPlan?.price || 0) +
        selectedAddons.reduce((prev, current) => prev + (current?.price || 0), 0),
      ContractId: currentContract?.contractId || '',
      SubscriptionType: 'New',
      OpportunityId: currentContract?.draftOportunityId || opportunityId || '',
      SubscriptionPlans: [
        {
          packageId: selectedPlan?.packageId || '',
          packageName: selectedPlan?.packageName || '',
          packageCode: '',
          startDate: '',
          endDate: '',
          period: '',
          amount: selectedPlan?.price || 0,
          isMainPackage: true,
          packagePeriod:
            selectedBilledOption === TRANSLATION_TEXT.billed_annually ||
            selectedBilledOption === TRANSLATION_TEXT.one_year
              ? '1 Year'
              : 'Monthly',
          addonCount: 0
        },
        ...selectedAddons.map(addon => ({
          packageId: addon?.packageId || '',
          packageName: addon?.packageName || '',
          packageCode: '',
          startDate: '',
          endDate: '',
          period: '',
          amount: addon?.price || 0,
          isMainPackage: false,
          packagePeriod: selectedBilledOption,
          addonCount: addon?.addonCount || 0
        }))
      ],
      AutoRenew: autoRenew,
      FirstName: form.values.firstName || '',
      LastName: form.values.lastName || '',
      Phone: form.values.phone || '',
      Email: form.values.email || '',
      CompanyName: form.values.companyName || '',
      CompanyType: form.values.companyType || '',
      Address: form.values.address || '',
      ServiceName: form.values.service || '',
      City: form.values.city || '',
      State: form.values.accountState || '',
      ZipCode: form.values.zipCode?.toString() || '',
      Country: form.values.companyCountry || '',
      MobilePhone: form.values.cellPhone || '',
      NewsBriefNewsletter: form.values.newsBriefNewsletter,
      TradeWireNewsletter: form.values.tradeWireNewsletter,
      JewelryConnoisseurNewsletter: form.values.weeklyJewelryNewsletter,
      BiMonthlyMagazineOnline: form.values.biMonthlyMagazineOnline,
      SmsConsent: form.values.smsTerms,
      SubscriptionPromoCode: appliedPromoCodeId || undefined,
      LeadSource: leadsource
    };
    return subscriptionRequestObject;
  };

  useRegistrationPrefill({ form, cacheForm, companyTypes });

  useEffect(() => {
    if (cacheForm && cacheForm.email) {
      form.setValues({ ...cacheForm, fromCache: true });

      execute({
        callback: getRegistrationCurrentContract,
        params: [cacheForm.email ?? '']
      });
    }
  }, [execute]);

  useEffect(() => {
    if (!subscriptionId && showErrorPopup.show) {
      if (CONFIG.__ENV === 'production') {
        customWindow.dataLayer = customWindow.dataLayer || [];
        customWindow.dataLayer.push({
          event: 'signup_fail',
          fail_reason: 'Invalid user',
          method: 'email'
        });
      }
      if (
        isPriceList &&
        showErrorPopup.show &&
        errorMessageType === errorListTypes.emailAlreadyExists
      ) {
        dispatch(
          openModal({
            modalClassName: 'subscriptionAssociatedModal',
            component: <SubscriptionAssociatedModal />
          })
        );
      } else if (showErrorPopup.show && errorMessageType === errorListTypes.hasPlanAndNotPrimary) {
        dispatch(
          openModal({
            component: <AcountExistsInCompanyErrorModal />
          })
        );
      } else if (showErrorPopup.show && errorMessageType === errorListTypes.hasSuspendedPlan) {
        dispatch(
          openModal({
            component: <SuspendedErrorModal />
          })
        );
      } else if (
        showErrorPopup.show &&
        (errorMessageType === errorListTypes.hasSubscriptionFound ||
          errorMessageType === errorListTypes.companyAlreadyExists ||
          errorMessageType === errorListTypes.emailLinkedToExistingAccount)
      ) {
        dispatch(
          openModal({
            modalClassName: 'confirmAndCheckoutNotSuccessModal',
            component: <ConfirmAndCheckoutNotSuccessModal errorType={errorMessageType} />
          })
        );
      } else if (errorMessageType === errorListTypes.registrationError) {
        dispatch(
          openModal({
            modalClassName: 'confirmAndCheckoutNotSuccessModal',
            component: <ConfirmAndCheckoutNotSuccessModal errorType={errorMessageType} />
          })
        );
      }
    }
  }, [showErrorPopup]);

  useEffect(() => {
    if (selectedPlan?.subscriptionType === 'Rapnet') {
      setIsPriceList(false);
    } else if (selectedPlan?.subscriptionType === 'PriceList') {
      setIsPriceList(true);
    }
  }, [selectedPlan]);

  useEffect(() => {
    if (paymentLink && opportunityId) {
      // If both paymentLink and opportunityId are available, open the payment section
      setIsConfirmed(true);
    }
  }, [paymentLink, opportunityId]);

  const [hubspotSubmitted, setHubspotSubmitted] = useState(false);

  useEffect(() => {
    if (subscriptionId) {
      execute({
        callback: LastSubscriptionRequest,
        params: [form.values.email || '']
      });
      setCacheForm(form.values);
    }
  }, [subscriptionId]);

  useEffect(() => {
    if (subscriptionId && selectedPlan && !hubspotSubmitted) {
      const hubSpotForm = CreateHubSpotForm({
        selectedPlan,
        form: form.values
      });
      dispatch(createHubSpotForm(hubSpotForm));
      setHubspotSubmitted(true);
    }
  }, [subscriptionId, selectedPlan, hubspotSubmitted]);

  useEffect(() => {
    if (CONFIG.__ENV === 'production' && sfContactId && subscriptionId) {
      customWindow.dataLayer = customWindow.dataLayer || [];

      // Determine event name based on plan query parameter
      const urlParams = new URLSearchParams(window.location.search);
      const planParam = urlParams.get('plan') || '';
      const eventName = 'signup_success';

      customWindow.dataLayer.push({
        event: eventName,
        user_id: sfContactId,
        plan_code: planParam,
        plan_bucket: planParam.startsWith('rapaporttrade') ? 'rapaporttrade' : 'price_list',
        amount: finalTotalPrice,
        method: 'email',
        leadUserData: {
          email: form.values.email || '',
          phone_number: form.values.phone || form.values.cellPhone || ''
        }
      });
    }
  }, [sfContactId, subscriptionId, finalTotalPrice]);

  useEffect(() => {
    if (isConfirmed) {
      setIsYourPlanActive(false);
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 1000);
    }
  }, [isConfirmed]);

  useEffect(() => {
    if (showSuccessPageForFreePlan) {
      setShowSuccessPage(true);
      removeCacheForm();
    }
  }, [showSuccessPageForFreePlan]);

  // Clear the pre-registration cookie once the user reaches any success page so
  // it cannot prefill or suppress HubSpot for a later, unrelated registration.
  useEffect(() => {
    if (showSuccessPage || showSuccessPageForPriceList) {
      deleteRapaportUserData();
    }
  }, [showSuccessPage, showSuccessPageForPriceList]);

  useEffect(() => {
    if (isYourPlanActive) setIsConfirmed(false);
  }, [isYourPlanActive]);

  useEffect(() => {
    if (
      isUnitedStatesCountry(form.values.companyCountry) ||
      selectedAddons.some(p => p?.packageName === 'Rapaport Magazine')
    ) {
      form.setFieldValue('hasMagazineAddon', true);
    } else {
      form.setFieldValue('hasMagazineAddon', false);
    }
  }, [selectedAddons, form.values.companyCountry]);

  useEffect(() => {
    setNumConfirmClicked(0);
    setHubspotSubmitted(false);
  }, [form.values.email]);

  useEffect(() => {
    // Send the compliance-docs email as soon as registration succeeds (when the
    // user proceeds to payment), not after the payment itself has completed.
    // Excluded for Price List registrations.
    if (
      subscriptionId &&
      form.values.email &&
      selectedPlan?.packageName &&
      selectedPlan?.subscriptionType !== 'PriceList'
    ) {
      execute({
        callback: sendComplianceDocsRequiredEmail,
        params: [form.values.email, selectedPlan?.packageName]
      });
    }
  }, [subscriptionId, execute]);

  const showPaymentIframe =
    isConfirmed && !isYourPlanActive && Boolean(paymentLink && opportunityId);

  const handlePreviousFromPayment = () => {
    setIsYourPlanActive(true);
    setIsConfirmed(false);
    setNumConfirmClicked(0);
  };

  return (
    <HeaderFooter
      isRapnet={
        (currentContract?.mainPackageName &&
          currentContract.mainPackageName.toLocaleLowerCase().includes('rapnet')) ||
        (selectedPlan?.packageName &&
          selectedPlan.packageName.toLocaleLowerCase().includes('rapnet')) ||
        false
      }
    >
      <Wrapper>
        {showSuccessPage === false &&
          showSuccessPageForPriceList === false &&
          contactMEThankYouScreenForPriceList === true && <ThankYouScreenForPriceList />}
        {showSuccessPage === true && <SuccessPage />}
        {showSuccessPage === false && showSuccessPageForPriceList === true && (
          <SuccessPageForPriceList />
        )}
        {showSuccessPage === false &&
          showSuccessPageForPriceList === false &&
          contactMEThankYouScreenForPriceList === false && (
            <PageCard>
              {showPaymentIframe ? (
                <PaymentDetailsSection
                  id="paymentSection"
                  setShowSuccessPage={(val: boolean) => setShowSuccessPage(val)}
                  setShowSuccessPageForPriceList={(val: boolean) =>
                    setShowSuccessPageForPriceList(val)
                  }
                  form={form}
                  removeCacheForm={removeCacheForm}
                  finalTotalPrice={finalTotalPrice}
                  onPrevious={handlePreviousFromPayment}
                />
              ) : (
                <>
                  <PageTitle>
                    {TRANSLATION_TEXT.confirm_your_details || 'Confirm Your Details'}
                  </PageTitle>
                  {!isPriceList && (
                    <ChargeNotice role="alert">
                      <ChargeNoticeIcon aria-hidden="true">ⓘ</ChargeNoticeIcon>
                      <div>
                        <ChargeNoticeTitle>
                          {TRANSLATION_TEXT.your_card_will_not_be_charged_today ||
                            'Your card will not be charged today'}
                        </ChargeNoticeTitle>
                        <ChargeNoticeText>
                          {TRANSLATION_TEXT.payment_processed_after_membership_approval ||
                            'Payment is only processed after Rapaport contacts you to verify and approve your membership.'}
                        </ChargeNoticeText>
                      </div>
                    </ChargeNotice>
                  )}
                  <PanelsRow>
                    <YourPlanContainer
                      setSelectedAddons={setSelectedAddons}
                      setSelectedPlan={setSelectedPlan}
                      selectedPlan={selectedPlan}
                      selectedBilledOption={selectedBilledOption}
                      setSelctedBilledOption={setSelctedBilledOption}
                      isYourPlanActive={isYourPlanActive}
                      setIsYourPlanActive={setIsYourPlanActive}
                      setNumConfirmClicked={setNumConfirmClicked}
                      form={form}
                      isPriceList={isPriceList}
                      selectedAddons={selectedAddons}
                      onPromoCodeApplied={setAppliedPromoCodeId}
                      setFinalTotalPrice={setFinalTotalPrice}
                      finalTotalPrice={finalTotalPrice}
                    />
                    <Container planBoxWidth={planBoxWidth}>
                      <JoinForm
                        form={form}
                        isConfirmed={isConfirmed}
                        selectedPlan={selectedPlan}
                        subscriptionId={subscriptionId}
                        numConfirmClicked={numConfirmClicked}
                        isPriceList={isPriceList}
                        finalTotalPrice={finalTotalPrice}
                      />
                    </Container>
                  </PanelsRow>
                </>
              )}
            </PageCard>
          )}
      </Wrapper>
    </HeaderFooter>
  );
};
export default JoinManger;
